import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { Consulta } from '../types';
import { ConsultasService } from '../services/consultasService';

interface UseConsultasReturn {
  consultas: Consulta[];
  totalPages: number;
  totalConsultas: number;
  consultasHoje: number;
  pacientesUnicos: number;
  consulta: Consulta | undefined;
  loading: boolean;
  error: string | null;
  get: (
    page?: number,
    limit?: number,
    query?: string,
    startDate?: string,
    endDate?: string
  ) => Promise<void>;
  getStats: (query?: string, startDate?: string, endDate?: string) => Promise<void>;
  deleteById: (id: string) => Promise<void>;
  getById: (id: string) => Promise<void>;
  upsert: (form: Partial<Consulta>) => Promise<Consulta | undefined>;
}

const useConsultas = (): UseConsultasReturn => {
  const navigate = useNavigate();
  const { currentOrganization } = useAuth();
  const [consultas, setConsultas] = useState<Consulta[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalConsultas, setTotalConsultas] = useState(0);
  const [consultasHoje, setConsultasHoje] = useState(0);
  const [pacientesUnicos, setPacientesUnicos] = useState(0);
  const [consulta, setConsulta] = useState<Consulta | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const get = useCallback(
    async (page = 0, limit = 10, query = '', startDate?: string, endDate?: string) => {
      if (!currentOrganization) return;

      setLoading(true);
      setError(null);
      try {
        const response = await ConsultasService.getConsultas(
          currentOrganization.id,
          page,
          limit,
          query,
          startDate,
          endDate
        );

        if (response.success && response.data) {
          setConsultas(response.data);
          setTotalPages(Math.ceil((response.count || 0) / limit));
        } else {
          setError(response.message);
          if (response.error?.message.includes('401')) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [navigate, currentOrganization]
  );

  const getStats = useCallback(
    async (query = '', startDate?: string, endDate?: string) => {
      if (!currentOrganization) return;

      try {
        const response = await ConsultasService.getConsultasStats(
          currentOrganization.id,
          query,
          startDate,
          endDate
        );

        if (response.success && response.data) {
          setTotalConsultas(response.data.totalConsultas);
          setConsultasHoje(response.data.consultasHoje);
          setPacientesUnicos(response.data.pacientesUnicos);
        }
      } catch (error) {
        // Don't set error state for stats, just log it
      }
    },
    [currentOrganization]
  );

  const deleteById = useCallback(
    async (id: string) => {
      if (!currentOrganization) return;

      setLoading(true);
      setError(null);
      try {
        const response = await ConsultasService.deleteConsulta(id, currentOrganization.id);

        if (!response.success) {
          setError(response.message);
          if (response.error?.message.includes('401')) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [navigate, currentOrganization]
  );

  const getById = useCallback(
    async (id: string) => {
      if (!currentOrganization) return;

      setLoading(true);
      setError(null);
      try {
        const response = await ConsultasService.getConsultaById(id, currentOrganization.id);

        if (response.success && response.data) {
          setConsulta(response.data);
        } else {
          setError(response.message);
          if (response.error?.message.includes('401')) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [navigate, currentOrganization]
  );

  const upsert = useCallback(
    async (form: Partial<Consulta>): Promise<Consulta | undefined> => {
      if (!currentOrganization) return;

      setError(null);
      try {
        const response = await ConsultasService.upsertConsulta(currentOrganization.id, form);

        if (response.success && response.data) {
          setConsulta(response.data);
          return response.data;
        } else {
          setError(response.message);
          if (response.error?.message.includes('401')) {
            navigate('/login');
          }
        }
      } catch (error) {
        setError((error as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [navigate, currentOrganization]
  );

  return {
    consultas,
    totalPages,
    totalConsultas,
    consultasHoje,
    pacientesUnicos,
    consulta,
    loading,
    error,
    get,
    getStats,
    deleteById,
    getById,
    upsert
  };
};

export default useConsultas;
